import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtMoney, formatApiErrorDetail } from "@/lib/api";
import MetricCard from "@/components/MetricCard";
import { Plus, Trash2, CircleDollarSign, Calendar, TrendingDown, Layers, Sparkles, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const DEBT_TYPES = [
  { v: "credit_card", l: "Credit Card" },
  { v: "loan", l: "Loan" },
  { v: "mortgage", l: "Mortgage" },
  { v: "line_of_credit", l: "Line of Credit" },
  { v: "other", l: "Other" },
];

function NewDebtForm({ onCreated, onClose }) {
  const [form, setForm] = useState({ name: "", lender: "", balance: "", interest_rate: "", minimum_payment: "", type: "loan" });
  const [minPaymentAuto, setMinPaymentAuto] = useState(true); // auto-fill for credit cards

  // Standard US credit-card minimum-payment formula (Chase/Capital-One):
  //   max($25, 1% of balance + monthly interest)
  // We recalculate whenever balance/APR/type changes AND user hasn't
  // manually overridden the field.
  useEffect(() => {
    if (form.type !== "credit_card" || !minPaymentAuto) return;
    const bal = parseFloat(form.balance);
    const apr = parseFloat(form.interest_rate);
    if (!bal || bal <= 0) return;
    const monthlyInterest = (apr && apr > 0) ? bal * (apr / 100) / 12 : 0;
    const oneP = bal * 0.01;
    const computed = Math.max(25, oneP + monthlyInterest);
    // Cap min at the outstanding balance itself
    const finalMin = Math.min(computed, bal);
    setForm((prev) => ({ ...prev, minimum_payment: finalMin.toFixed(2) }));
  }, [form.balance, form.interest_rate, form.type, minPaymentAuto]);

  const onTypeChange = (v) => {
    // When user switches TO credit_card, re-enable auto so the recompute
    // effect kicks in. When leaving credit_card, clear the auto flag so
    // subsequent manual edits stick.
    setForm({ ...form, type: v });
    setMinPaymentAuto(v === "credit_card");
  };

  const onMinPaymentChange = (e) => {
    // Any manual edit disables auto-fill until they switch type again
    if (form.type === "credit_card") setMinPaymentAuto(false);
    setForm({ ...form, minimum_payment: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/debts", {
        ...form,
        balance: parseFloat(form.balance),
        interest_rate: parseFloat(form.interest_rate),
        minimum_payment: parseFloat(form.minimum_payment),
      });
      toast.success("Debt added");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <form onSubmit={submit} className="card-surface p-6 space-y-4 mb-6" data-testid="new-debt-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm">
          <div className="font-medium mb-1.5">Name</div>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="debt-name-input"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest" />
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5">Lender</div>
          <input value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })}
            data-testid="debt-lender-input"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest" />
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5">Balance</div>
          <input type="number" step="0.01" min="0.01" required value={form.balance}
            onChange={(e) => setForm({ ...form, balance: e.target.value })}
            data-testid="debt-balance-input"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 num outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest" />
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5">Interest rate (APR %)</div>
          <input type="number" step="0.01" min="0" required value={form.interest_rate}
            onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
            data-testid="debt-interest-input"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 num outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest" />
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5 flex items-center justify-between">
            <span>Minimum monthly payment</span>
            {form.type === "credit_card" && minPaymentAuto && (
              <span className="text-[10px] font-normal text-forest bg-forest/10 rounded px-1.5 py-0.5" data-testid="min-payment-auto-badge">
                Auto-calculated
              </span>
            )}
          </div>
          <input type="number" step="0.01" min="0" required value={form.minimum_payment}
            onChange={onMinPaymentChange}
            data-testid="debt-min-payment-input"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 num outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest" />
          {form.type === "credit_card" && (
            <span className="text-[11px] text-ink-lighter mt-1 block">
              {minPaymentAuto
                ? "Standard US credit-card formula: max of $25 or 1% of balance + monthly interest."
                : "Manually overridden — switch Type back to Credit Card to reset."}
            </span>
          )}
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5">Type</div>
          <select value={form.type} onChange={(e) => onTypeChange(e.target.value)}
            data-testid="debt-type-select"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest">
            {DEBT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </label>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-debt">Cancel</button>
        <button type="submit" className="btn-forest" data-testid="save-debt-button">Add debt</button>
      </div>
    </form>
  );
}

export default function Debts() {
  const [debts, setDebts] = useState([]);
  const [plan, setPlan] = useState(null);
  const [strategy, setStrategy] = useState("avalanche");
  const [extra, setExtra] = useState(200);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const load = async () => {
    setLoading(true);
    const [d, p] = await Promise.all([
      api.get("/debts"),
      api.post("/debts/payoff-plan", { strategy, extra_monthly_payment: extra }),
    ]);
    setDebts(d.data); setPlan(p.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [strategy, extra]);

  // Command-palette: ?ai=1 triggers the AI debt plan modal
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("ai") === "1" && !loading && debts.length > 0) {
      generateAiPlan();
      const next = new URLSearchParams(params);
      next.delete("ai");
      setParams(next, { replace: true });
    }
  }, [params, loading, debts.length]);

  const remove = async (id) => {
    await api.delete(`/debts/${id}`);
    toast.success("Removed");
    load();
  };

  const generateAiPlan = async () => {
    if (debts.length === 0) {
      toast.error("Add at least one debt to generate an AI plan.");
      return;
    }
    setAiOpen(true);
    setAiLoading(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/debts/ai-plan");
      setAiResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI plan failed");
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const totalBalance = debts.reduce((s, d) => s + d.balance, 0);
  const totalMin = debts.reduce((s, d) => s + d.minimum_payment, 0);

  // sample timeline points for chart
  const chartData = (plan?.timeline || []).filter((_, i, arr) => i === 0 || i === arr.length - 1 || i % Math.ceil(arr.length / 24) === 0)
    .map((m) => ({ month: m.month, balance: m.total_balance }));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="debts-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Debt</div>
          <h1 className="font-heading text-4xl font-medium tracking-tight">Time to be debt-free.</h1>
          <p className="text-ink-light mt-2">Compare strategies. See the exact payoff date.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={generateAiPlan} disabled={aiLoading} className="btn-secondary inline-flex items-center gap-2" data-testid="ai-debt-plan-btn">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Quickest way out (AI)
          </button>
          <button onClick={() => setShowNew(!showNew)} className="btn-forest inline-flex items-center gap-2" data-testid="add-debt-button">
            <Plus className="w-4 h-4" /> Add debt
          </button>
        </div>
      </div>

      {showNew && <NewDebtForm onCreated={load} onClose={() => setShowNew(false)} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Total Balance" value={fmtMoney(totalBalance)} testid="debt-total-balance" icon={CircleDollarSign} accent="text-ochre" />
        <MetricCard label="Monthly Minimums" value={fmtMoney(totalMin)} testid="debt-monthly-min" icon={Calendar} />
        <MetricCard label="Payoff (months)" value={plan?.months_to_payoff || "—"} testid="debt-payoff-months" icon={TrendingDown} accent="text-forest"
          sub={plan?.months_to_payoff ? `${(plan.months_to_payoff / 12).toFixed(1)} years` : ""} />
        <MetricCard label="Total Interest" value={fmtMoney(plan?.total_interest_paid || 0)} testid="debt-total-interest" icon={Layers} />
      </div>

      <div className="card-surface p-6 mb-6" data-testid="strategy-card">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <div className="overline mb-1.5">Strategy</div>
            <h3 className="font-heading text-2xl font-medium">Choose your attack plan</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-paper rounded-lg p-1 border border-border">
              <button onClick={() => setStrategy("avalanche")} data-testid="strategy-avalanche"
                className={`px-4 py-1.5 rounded-md text-sm transition-colors ${strategy === "avalanche" ? "bg-white text-forest font-medium shadow-sm" : "text-ink-light"}`}>
                Avalanche
              </button>
              <button onClick={() => setStrategy("snowball")} data-testid="strategy-snowball"
                className={`px-4 py-1.5 rounded-md text-sm transition-colors ${strategy === "snowball" ? "bg-white text-forest font-medium shadow-sm" : "text-ink-light"}`}>
                Snowball
              </button>
            </div>
            <label className="text-sm flex items-center gap-2">
              <span className="text-ink-light whitespace-nowrap">Extra / mo</span>
              <input type="number" min="0" step="50" value={extra} onChange={(e) => setExtra(parseFloat(e.target.value || 0))}
                data-testid="extra-payment-input"
                className="w-24 bg-white border border-border rounded-lg px-3 py-1.5 num outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest" />
            </label>
          </div>
        </div>
        <p className="text-sm text-ink-light mb-5">
          {strategy === "avalanche"
            ? "Avalanche: kill highest-APR debts first. Pays the least interest overall."
            : "Snowball: kill smallest balances first. Best psychological wins."}
        </p>
        <div className="h-64">
          {chartData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8A8A8A" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8A8A8A" }} tickLine={false} axisLine={false} tickFormatter={(v) => "$" + (v/1000).toFixed(0) + "k"} />
                <Tooltip formatter={(v) => fmtMoney(v)} labelFormatter={(l) => `Month ${l}`}
                  contentStyle={{ background: "#fff", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="balance" stroke="#0F4C3A" strokeWidth={2.5} dot={false} name="Remaining balance" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? <div className="p-8 text-ink-lighter">Loading…</div> :
         debts.length === 0 ? (
          <div className="p-12 text-center" data-testid="debts-empty">
            <div className="text-ink-lighter mb-3">No debts tracked yet</div>
            <button onClick={() => setShowNew(true)} className="btn-forest">Add your first</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper">
                <tr className="text-left text-xs uppercase tracking-wider text-ink-lighter border-b border-border">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Lender</th>
                  <th className="px-5 py-3 text-right">Balance</th>
                  <th className="px-5 py-3 text-right">APR</th>
                  <th className="px-5 py-3 text-right">Min Pay</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {debts.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0 hover:bg-paper/60" data-testid={`debt-row-${d.id}`}>
                    <td className="px-5 py-3 font-medium">{d.name}</td>
                    <td className="px-5 py-3 text-ink-light">{d.lender || "—"}</td>
                    <td className="px-5 py-3 num text-right">{fmtMoney(d.balance)}</td>
                    <td className="px-5 py-3 num text-right text-ochre">{d.interest_rate.toFixed(2)}%</td>
                    <td className="px-5 py-3 num text-right">{fmtMoney(d.minimum_payment)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => remove(d.id)} className="text-ink-lighter hover:text-destructive" data-testid={`delete-debt-${d.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {aiOpen && (
        <AiDebtPlanModal
          onClose={() => setAiOpen(false)}
          loading={aiLoading}
          result={aiResult}
        />
      )}
    </div>
  );
}

// -- Markdown renderer + AI plan modal ---------------------------------------

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderInline(s) {
  // Bold **x** and italic *x* — apply on already-escaped HTML.
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
}

/** Tiny markdown renderer scoped to the LLM's expected output:
 *  ## H2, - bullets, 1. numbered lists, **bold**, paragraphs. */
function SimpleMarkdown({ text }) {
  const src = (text || "").trim();
  const lines = src.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();
    if (!line.trim()) { i += 1; continue; }
    // Heading
    const h2 = line.match(/^##\s+(.*)$/);
    const h1 = line.match(/^#\s+(.*)$/);
    if (h2) { blocks.push({ t: "h2", c: h2[1] }); i += 1; continue; }
    if (h1) { blocks.push({ t: "h1", c: h1[1] }); i += 1; continue; }
    // Numbered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ t: "ol", c: items });
      continue;
    }
    // Bullet list
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ t: "ul", c: items });
      continue;
    }
    // Paragraph — collect consecutive non-empty lines
    const para = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !/^(#{1,2}\s|[-*]\s|\d+\.\s)/.test(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ t: "p", c: para.join(" ") });
  }

  return (
    <div className="prose-tight" data-testid="ai-plan-markdown">
      {blocks.map((b, idx) => {
        if (b.t === "h1") {
          return <h2 key={idx} className="font-heading text-2xl font-medium mt-6 mb-2">{b.c}</h2>;
        }
        if (b.t === "h2") {
          return <h3 key={idx} className="font-heading text-lg font-medium text-forest mt-5 mb-2">{b.c}</h3>;
        }
        if (b.t === "ol") {
          return (
            <ol key={idx} className="list-decimal ml-5 space-y-1 text-sm text-ink mb-3">
              {b.c.map((it, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: renderInline(escapeHtml(it)) }} />
              ))}
            </ol>
          );
        }
        if (b.t === "ul") {
          return (
            <ul key={idx} className="list-disc ml-5 space-y-1 text-sm text-ink mb-3">
              {b.c.map((it, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: renderInline(escapeHtml(it)) }} />
              ))}
            </ul>
          );
        }
        return (
          <p
            key={idx}
            className="text-sm text-ink leading-relaxed mb-3"
            dangerouslySetInnerHTML={{ __html: renderInline(escapeHtml(b.c)) }}
          />
        );
      })}
    </div>
  );
}

function AiDebtPlanModal({ onClose, loading, result }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ai-debt-plan-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI advisor</div>
            <h3 className="font-heading text-xl font-medium">Your quickest way out of debt</h3>
            <p className="text-sm text-ink-light mt-1">
              Grounded in your real balances, APRs, and cash flow. Powered by Claude Sonnet 4.5.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="ai-plan-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading || !result ? (
            <div className="flex flex-col items-center justify-center py-16 text-ink-lighter" data-testid="ai-plan-loading">
              <Loader2 className="w-6 h-6 animate-spin text-forest mb-3" />
              <div className="text-sm">Analyzing your debts and cash flow…</div>
              <div className="text-xs mt-1">Usually takes 5–10 seconds</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <div className="bg-paper rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-ink-lighter">Winning strategy</div>
                  <div className="font-heading text-lg mt-0.5 capitalize" data-testid="ai-winning-strategy">{result.winning_strategy}</div>
                </div>
                <div className="bg-paper rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-ink-lighter">Extra / month</div>
                  <div className="font-heading text-lg mt-0.5 num">{fmtMoney(result.suggested_extra_monthly_payment)}</div>
                </div>
                <div className="bg-paper rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-ink-lighter">Payoff (best)</div>
                  <div className="font-heading text-lg mt-0.5 num">
                    {result.winning_strategy === "avalanche" ? result.avalanche.months_to_payoff : result.snowball.months_to_payoff} mo
                  </div>
                </div>
                <div className="bg-paper rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-ink-lighter">Interest saved</div>
                  <div className="font-heading text-lg mt-0.5 num text-forest">
                    {fmtMoney(Math.max(0, result.minimums_only.total_interest_paid - Math.min(result.avalanche.total_interest_paid, result.snowball.total_interest_paid)))}
                  </div>
                </div>
              </div>
              <SimpleMarkdown text={result.plan_markdown} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
